import { getActiveTitle, getEarnedTiers, getNextTier } from '../save/CosmeticUnlocks.js';
import { getDailyChallengeProgress, getDailyChallenges, getTodayUtcDateStr } from '../save/DailyChallenges.js';
import {
  computePersonalRecords,
  formatScalar,
  formatStreak,
  formatVictoryTime,
} from '../save/PersonalRecords.js';
import { computeProfileStats } from '../save/Profile.js';
import { loadElo, loadPeakElo, tierFor, tierProgress } from '../save/Rank.js';
import {
  ARCHETYPES,
  ARCHETYPE_COLOR,
  ARCHETYPE_LABEL,
  getAllMasteries,
} from '../save/SquadMastery.js';
import { loadRows } from '../save/Telemetry.js';
import { loadXpState } from '../save/Xp.js';

// V3 Profile panel — DOM overlay above the Phaser canvas. Follows the same
// pattern as SettingsPanel / EndOfMatch: pure presentation, reads from
// save modules, no Phaser objects. Mounted once; show()/hide() toggle it.
//
// V2 Tempo redesign — adopts the main-menu's neon hex aesthetic so the
// panel reads as part of the same product, not a debug overlay. Adds
// Personal Records section + Doctrine Mastery + recent-form sparkline so
// returning players have multiple "I beat my best" hooks to chase.

const PANEL_ID = 'profile-panel';
const STYLE_ID = 'profile-panel-styles';
const HISTORY_LIMIT = 8;

const DOCTRINE_LABELS: Record<string, string> = {
  vanguard: 'Vanguard',
  saboteur: 'Saboteur',
  bulwark: 'Bulwark',
};

export class ProfilePanel {
  private root: HTMLDivElement;

  constructor() {
    injectStyles();
    this.root = ensureRoot();
    this.hide();
  }

  show(): void {
    this.refresh();
    this.root.style.display = 'flex';
  }

  hide(): void {
    this.root.style.display = 'none';
  }

  private refresh(): void {
    this.root.innerHTML = '';

    const rows = loadRows();
    const stats = computeProfileStats(rows);
    const today = getTodayUtcDateStr();
    const challenges = getDailyChallenges(today);
    const progress = getDailyChallengeProgress(challenges, rows, today);
    const activeTitle = getActiveTitle(stats.totalMatches);
    const earnedTiers = getEarnedTiers(stats.totalMatches);
    const nextTier = getNextTier(stats.totalMatches);
    const records = computePersonalRecords(rows);
    const xp = loadXpState();
    const elo = loadElo();
    const peakElo = loadPeakElo();
    const rankTier = tierFor(elo);
    const rankProgress = tierProgress(elo);
    const masteries = getAllMasteries();
    const history = rows.slice(-HISTORY_LIMIT).reverse();

    const card = document.createElement('div');
    card.className = 'pp__card';

    // ── Header: identity + LV badge + active title ─────────────────────────
    const header = document.createElement('div');
    header.className = 'pp__header';
    header.innerHTML = `
      <div class="pp__header-left">
        <div class="pp__header-level">
          <span class="pp__header-level-num">${xp.level}</span>
          <span class="pp__header-level-word">LEVEL</span>
        </div>
        <div class="pp__header-title-block">
          <div class="pp__header-title">${escapeHtml(activeTitle ?? 'Operator')}</div>
          <div class="pp__header-uuid">ID · …${stats.uuid.slice(-8)}</div>
        </div>
      </div>
      <button type="button" class="pp__close" aria-label="Close profile">×</button>
    `;
    const closeTopBtn = header.querySelector('.pp__close');
    if (closeTopBtn !== null) {
      closeTopBtn.addEventListener('click', () => this.hide());
    }
    card.appendChild(header);

    // ── Rank chip + progress ───────────────────────────────────────────────
    const rankPct = (rankProgress.ratio * 100).toFixed(1);
    const rankSection = document.createElement('div');
    rankSection.className = 'pp__rank';
    const rankNextLine = rankProgress.nextTier !== null
      ? `${rankProgress.toNext} ELO to <strong style="color:${rankProgress.nextTier.color}">${rankProgress.nextTier.name}</strong>`
      : 'Peak tier reached';
    rankSection.innerHTML = `
      <div class="pp__section-label">Skill Rank</div>
      <div class="pp__rank-head">
        <span class="pp__rank-name" style="color:${rankTier.color};text-shadow:0 0 12px ${rankTier.glow}">${escapeHtml(rankTier.name.toUpperCase())}</span>
        <span class="pp__rank-elo">${elo} ELO${peakElo > elo ? ` · Peak ${peakElo}` : ''}</span>
      </div>
      <div class="pp__rank-bar">
        <div class="pp__rank-bar-fill" style="width:${rankPct}%;background:linear-gradient(90deg, ${rankTier.color}, ${rankProgress.nextTier?.color ?? rankTier.color});box-shadow:0 0 10px ${rankTier.glow}"></div>
      </div>
      <div class="pp__rank-next">${rankNextLine}</div>
    `;
    card.appendChild(rankSection);

    // ── XP progress bar with next-tier tease ───────────────────────────────
    const xpPct = xp.xpToNextLevel > 0
      ? Math.max(0, Math.min(100, (xp.xpIntoLevel / xp.xpToNextLevel) * 100))
      : 100;
    const nextTierLabel = nextTier !== null
      ? `Next title: <strong>${escapeHtml(nextTier.title)}</strong> in ${Math.max(0, nextTier.matchThreshold - stats.totalMatches)} match${Math.max(0, nextTier.matchThreshold - stats.totalMatches) === 1 ? '' : 'es'}`
      : 'All titles unlocked';
    const xpBar = document.createElement('div');
    xpBar.className = 'pp__xpbar-wrap';
    xpBar.innerHTML = `
      <div class="pp__xpbar">
        <div class="pp__xpbar-fill" style="width:${xpPct.toFixed(1)}%"></div>
      </div>
      <div class="pp__xpbar-foot">
        <span>${xp.xpIntoLevel} / ${xp.xpToNextLevel} XP</span>
        <span>${nextTierLabel}</span>
      </div>
    `;
    card.appendChild(xpBar);

    // ── Personal Records grid ──────────────────────────────────────────────
    const winRateText = records.winRatePct === null ? '—' : `${records.winRatePct}%`;
    const recordsGrid = document.createElement('div');
    recordsGrid.className = 'pp__records';
    recordsGrid.innerHTML = `
      <div class="pp__records-head">Personal Records</div>
      <div class="pp__records-grid">
        ${recordCell('Matches', formatScalar(records.totalMatches), '')}
        ${recordCell('Win rate', winRateText, 'cyan')}
        ${recordCell('Current streak', formatStreak(records.currentWinStreak), 'amber')}
        ${recordCell('Longest streak', formatStreak(records.longestWinStreak), 'amber')}
        ${recordCell('Fastest win', formatVictoryTime(records.fastestVictorySec), '')}
        ${recordCell('Peak signal', formatScalar(records.peakSignalScore), '')}
        ${recordCell('Most squads', formatScalar(records.peakSquadsBuilt), '')}
        ${recordCell('Most flips', formatScalar(records.peakRelayFlips), '')}
      </div>
    `;
    card.appendChild(recordsGrid);

    // ── Recent form sparkline ──────────────────────────────────────────────
    if (records.totalMatches > 0) {
      const form = document.createElement('div');
      form.className = 'pp__form';
      const cells = records.recentForm
        .map((r) => `<span class="pp__form-cell pp__form-cell--${r === 'W' ? 'w' : 'l'}" title="${r === 'W' ? 'Win' : 'Loss'}"></span>`)
        .join('');
      const emptyCount = Math.max(0, 10 - records.recentForm.length);
      const empties = '<span class="pp__form-cell pp__form-cell--empty"></span>'.repeat(emptyCount);
      form.innerHTML = `
        <div class="pp__form-label">Recent form · last 10</div>
        <div class="pp__form-row">${cells}${empties}</div>
      `;
      card.appendChild(form);
    }

    // ── Squad Mastery ──────────────────────────────────────────────────────
    const totalMastery = ARCHETYPES.reduce((acc, a) => acc + masteries[a].count, 0);
    if (totalMastery > 0) {
      const squadMastery = document.createElement('div');
      squadMastery.className = 'pp__sqmastery';
      const rowsHtml = ARCHETYPES.map((a) => {
        const m = masteries[a];
        const color = ARCHETYPE_COLOR[a];
        const pct = Math.round(m.ratio * 100);
        const nextLine = m.nextTier !== null
          ? `${m.tier.name} · ${m.toNext} to ${m.nextTier.name}`
          : `${m.tier.name} · Max`;
        return `
          <div class="pp__sq-row">
            <span class="pp__sq-name" style="color:${color}">${escapeHtml(ARCHETYPE_LABEL[a])}</span>
            <span class="pp__sq-bar">
              <span class="pp__sq-bar-fill" style="width:${pct}%;background:linear-gradient(90deg, ${color}aa, ${color});box-shadow:0 0 6px ${color}55"></span>
            </span>
            <span class="pp__sq-count">${formatScalar(m.count)}</span>
            <span class="pp__sq-tier">${escapeHtml(nextLine)}</span>
          </div>
        `;
      }).join('');
      squadMastery.innerHTML = `
        <div class="pp__section-label">Squad Mastery</div>
        ${rowsHtml}
      `;
      card.appendChild(squadMastery);
    }

    // ── Doctrine mastery ───────────────────────────────────────────────────
    const masteryEntries = Object.entries(records.doctrineMastery).sort(
      (a, b) => b[1].played - a[1].played,
    );
    if (masteryEntries.length > 0) {
      const mastery = document.createElement('div');
      mastery.className = 'pp__mastery';
      const rowsHtml = masteryEntries
        .map(([key, data]) => {
          const pct = Math.round(data.winRate * 100);
          const display = DOCTRINE_LABELS[key] ?? key;
          return `
            <div class="pp__mastery-row">
              <span class="pp__mastery-name">${escapeHtml(display)}</span>
              <span class="pp__mastery-bar">
                <span class="pp__mastery-bar-fill" style="width:${pct}%"></span>
              </span>
              <span class="pp__mastery-pct">${pct}%</span>
              <span class="pp__mastery-played">(${data.wins}/${data.played})</span>
            </div>
          `;
        })
        .join('');
      mastery.innerHTML = `
        <div class="pp__section-label">Doctrine mastery</div>
        ${rowsHtml}
      `;
      card.appendChild(mastery);
    }

    // ── Titles (cosmetic unlocks) ──────────────────────────────────────────
    const titlesSection = document.createElement('div');
    titlesSection.className = 'pp__titles';
    titlesSection.innerHTML = `<div class="pp__section-label">Titles</div>`;
    if (earnedTiers.length === 0) {
      const toGo = Math.max(0, 5 - stats.totalMatches);
      const empty = document.createElement('div');
      empty.className = 'pp__empty';
      empty.textContent = `Play ${toGo} more match${toGo === 1 ? '' : 'es'} to earn your first title`;
      titlesSection.appendChild(empty);
    } else {
      const row = document.createElement('div');
      row.className = 'pp__badges';
      for (const tier of earnedTiers) {
        const badge = document.createElement('div');
        badge.className = 'pp__badge pp__badge--earned';
        badge.title = tier.description;
        badge.textContent = tier.title;
        row.appendChild(badge);
      }
      if (nextTier !== null) {
        const toGo = Math.max(0, nextTier.matchThreshold - stats.totalMatches);
        const badge = document.createElement('div');
        badge.className = 'pp__badge pp__badge--locked';
        badge.title = `${toGo} more match${toGo === 1 ? '' : 'es'} to unlock`;
        badge.textContent = `${nextTier.title} · ${toGo} to go`;
        row.appendChild(badge);
      }
      titlesSection.appendChild(row);
    }
    card.appendChild(titlesSection);

    // ── Daily challenges ───────────────────────────────────────────────────
    const challengeSection = document.createElement('div');
    challengeSection.className = 'pp__dailies';
    challengeSection.innerHTML = `<div class="pp__section-label">Daily — ${today}</div>`;
    for (const p of progress) {
      const row = document.createElement('div');
      row.className = `pp__challenge${p.done ? ' pp__challenge--done' : ''}`;
      const check = document.createElement('span');
      check.className = 'pp__challenge-check';
      check.textContent = p.done ? '✓' : '○';
      check.setAttribute('aria-hidden', 'true');
      const text = document.createElement('span');
      text.textContent = p.def.goal > 1
        ? `${p.def.label} (${p.current}/${p.def.goal})`
        : p.def.label;
      row.appendChild(check);
      row.appendChild(text);
      challengeSection.appendChild(row);
    }
    card.appendChild(challengeSection);

    // ── Match history ──────────────────────────────────────────────────────
    const historySection = document.createElement('div');
    historySection.className = 'pp__history';
    historySection.innerHTML = `<div class="pp__section-label">Recent Matches</div>`;
    if (history.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'pp__empty';
      empty.textContent = 'No matches played yet';
      historySection.appendChild(empty);
    } else {
      for (const row of history) {
        // V2 Tempo hot-fix: stale rows can have outcome/doctrines/mapId
        // undefined; coerce so the history list renders cleanly instead
        // of throwing.
        const outcomeObj = (row as { outcome?: { winner?: unknown } }).outcome;
        const won = outcomeObj !== undefined && outcomeObj !== null && outcomeObj.winner === 0;
        const item = document.createElement('div');
        item.className = `pp__hist-item${won ? ' pp__hist-item--win' : ' pp__hist-item--loss'}`;
        const outcomeEl = document.createElement('span');
        outcomeEl.className = 'pp__hist-outcome';
        outcomeEl.textContent = won ? 'W' : 'L';
        const mins = Math.floor(row.matchSeconds / 60);
        const secs = Math.round(row.matchSeconds % 60);
        const doctrineVal = Array.isArray(row.doctrines) ? row.doctrines[0] ?? null : null;
        const docStr = doctrineVal !== null ? ` · ${doctrineVal}` : '';
        const mapLabel = typeof row.mapId === 'string' ? row.mapId : '—';
        const detail = document.createElement('span');
        detail.className = 'pp__hist-detail';
        detail.textContent = `${mapLabel} · ${mins}:${secs.toString().padStart(2, '0')}${docStr}`;
        const dateEl = document.createElement('span');
        dateEl.className = 'pp__hist-date';
        dateEl.textContent = row.recordedAt.slice(5, 10); // MM-DD
        item.appendChild(outcomeEl);
        item.appendChild(detail);
        item.appendChild(dateEl);
        historySection.appendChild(item);
      }
    }
    card.appendChild(historySection);

    // ── Footer close ───────────────────────────────────────────────────────
    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'pp__close-btn';
    closeBtn.textContent = 'Close';
    closeBtn.addEventListener('click', () => this.hide());
    card.appendChild(closeBtn);

    this.root.appendChild(card);
  }
}

function recordCell(label: string, value: string, tone: '' | 'cyan' | 'amber'): string {
  const toneCls = tone === '' ? '' : ` pp__rec-num--${tone}`;
  return `
    <div class="pp__rec-cell">
      <div class="pp__rec-num${toneCls}">${escapeHtml(value)}</div>
      <div class="pp__rec-lbl">${escapeHtml(label)}</div>
    </div>
  `;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      case "'":
        return '&#39;';
      default:
        return c;
    }
  });
}

function ensureRoot(): HTMLDivElement {
  const existing = document.getElementById(PANEL_ID);
  if (existing instanceof HTMLDivElement) return existing;
  const div = document.createElement('div');
  div.id = PANEL_ID;
  document.body.appendChild(div);
  return div;
}

function injectStyles(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
#${PANEL_ID} {
  position: absolute;
  inset: 0;
  display: none;
  align-items: flex-start;
  justify-content: center;
  z-index: 40;
  background: rgba(2, 4, 6, 0.86);
  font-family: 'Rajdhani', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  color: #eef4ff;
  overflow-y: auto;
  padding: 28px 12px 40px;
}
.pp__card {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 22px 26px 24px;
  width: min(560px, 96vw);
  position: relative;
  background:
    radial-gradient(ellipse 100% 100% at 0% 0%, rgba(30, 167, 255, 0.16), transparent 60%),
    radial-gradient(ellipse 100% 100% at 100% 100%, rgba(255, 122, 90, 0.12), transparent 60%),
    linear-gradient(180deg, rgba(10, 19, 34, 0.96), rgba(5, 9, 17, 0.98));
  border: 1px solid rgba(174, 240, 255, 0.28);
  clip-path: polygon(16px 0, 100% 0, 100% calc(100% - 16px), calc(100% - 16px) 100%, 0 100%, 0 16px);
  box-shadow:
    0 0 0 1px rgba(255,255,255,0.06) inset,
    0 0 44px rgba(30,167,255,0.12) inset,
    0 16px 50px rgba(0,0,0,0.7);
}
.pp__card::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: linear-gradient(180deg, rgba(174,240,255,0.04) 1px, transparent 1px);
  background-size: 100% 24px;
  opacity: 0.4;
  pointer-events: none;
}
.pp__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(174, 240, 255, 0.16);
  position: relative;
  z-index: 1;
}
.pp__header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}
.pp__header-level {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 64px;
  padding: 6px 10px;
  border: 1px solid rgba(174, 240, 255, 0.30);
  clip-path: polygon(8px 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%, 0 8px);
  background: rgba(10, 19, 34, 0.85);
  box-shadow: 0 0 18px rgba(30, 167, 255, 0.20) inset;
}
.pp__header-level-num {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 28px;
  line-height: 1;
  color: #aef0ff;
  text-shadow: 0 0 14px rgba(110,220,255,0.55), 0 0 28px rgba(30,167,255,0.35);
  font-weight: 700;
  letter-spacing: 0.02em;
}
.pp__header-level-word {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 9px;
  letter-spacing: 0.28em;
  color: #6fdcff;
  text-transform: uppercase;
  margin-top: 2px;
}
.pp__header-title-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.pp__header-title {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #ffe680;
  text-shadow: 0 0 12px rgba(255,230,128,0.35);
}
.pp__header-uuid {
  font-size: 10px;
  color: #6a7d96;
  letter-spacing: 0.10em;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.pp__close {
  background: transparent;
  border: 1px solid rgba(174,240,255,0.28);
  color: #a3b8d2;
  font-size: 20px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: inherit;
  padding: 0;
  line-height: 1;
}
.pp__close:hover {
  color: #fff;
  border-color: rgba(174,240,255,0.55);
}

.pp__rank, .pp__sqmastery {
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  z-index: 1;
}
.pp__rank-head {
  display: flex;
  align-items: baseline;
  gap: 12px;
}
.pp__rank-name {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.22em;
  flex: 1;
}
.pp__rank-elo {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  color: #a3b8d2;
  letter-spacing: 0.10em;
}
.pp__rank-bar {
  height: 8px;
  background: rgba(5, 9, 17, 0.85);
  border: 1px solid rgba(174, 240, 255, 0.20);
  border-radius: 1px;
  overflow: hidden;
}
.pp__rank-bar-fill {
  height: 100%;
  transition: width 480ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.pp__rank-next {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10px;
  color: #a3b8d2;
  letter-spacing: 0.10em;
}
.pp__rank-next strong { font-weight: 700; }

.pp__sq-row {
  display: grid;
  grid-template-columns: 80px 1fr 50px 1fr;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  color: #a3b8d2;
}
.pp__sq-name {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-size: 11px;
  font-weight: 700;
}
.pp__sq-bar {
  display: block;
  height: 6px;
  background: rgba(5, 9, 17, 0.85);
  border: 1px solid rgba(174, 240, 255, 0.18);
  border-radius: 1px;
  overflow: hidden;
}
.pp__sq-bar-fill {
  display: block;
  height: 100%;
}
.pp__sq-count {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  text-align: right;
  font-size: 11px;
  color: #eef4ff;
}
.pp__sq-tier {
  font-size: 10px;
  color: #6a7d96;
  letter-spacing: 0.06em;
  text-align: right;
}

.pp__xpbar-wrap {
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
  z-index: 1;
}
.pp__xpbar {
  position: relative;
  height: 8px;
  background: rgba(5, 9, 17, 0.85);
  border: 1px solid rgba(106, 216, 255, 0.30);
  border-radius: 1px;
  overflow: hidden;
}
.pp__xpbar-fill {
  height: 100%;
  background: linear-gradient(90deg, #1ea7ff, #6fdcff 60%, #ffe680);
  box-shadow: 0 0 14px rgba(30, 167, 255, 0.45);
  transition: width 480ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.pp__xpbar-foot {
  display: flex;
  justify-content: space-between;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  color: #a3b8d2;
}
.pp__xpbar-foot strong { color: #ffe680; font-weight: 700; }

.pp__records {
  position: relative;
  z-index: 1;
}
.pp__records-head {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10px;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  color: #6fdcff;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(174,240,255,0.12);
}
.pp__records-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
}
.pp__rec-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px 4px;
  background: rgba(10, 19, 34, 0.55);
  border: 1px solid rgba(174, 240, 255, 0.15);
  clip-path: polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px);
  min-width: 0;
}
.pp__rec-num {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 16px;
  color: #eef4ff;
  letter-spacing: 0.04em;
  line-height: 1.1;
  text-align: center;
}
.pp__rec-num--cyan { color: #6fdcff; }
.pp__rec-num--amber { color: #ffe680; }
.pp__rec-lbl {
  font-size: 9px;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #6a7d96;
  margin-top: 5px;
  text-align: center;
  white-space: nowrap;
}

.pp__form {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  position: relative;
  z-index: 1;
}
.pp__form-label {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 9px;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  color: #6a7d96;
}
.pp__form-row {
  display: inline-flex;
  gap: 4px;
}
.pp__form-cell {
  width: 16px;
  height: 16px;
  border-radius: 1px;
  border: 1px solid rgba(174,240,255,0.18);
}
.pp__form-cell--w {
  background: #1ea7ff;
  border-color: #6fdcff;
  box-shadow: 0 0 6px rgba(30,167,255,0.55);
}
.pp__form-cell--l {
  background: #ff2a2a;
  border-color: #ff7a5a;
  box-shadow: 0 0 6px rgba(255,42,42,0.45);
}
.pp__form-cell--empty {
  background: transparent;
  border-color: rgba(174,240,255,0.10);
}

.pp__mastery, .pp__titles, .pp__dailies, .pp__history {
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  z-index: 1;
}
.pp__section-label {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10px;
  letter-spacing: 0.20em;
  text-transform: uppercase;
  color: #6fdcff;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(174,240,255,0.12);
}
.pp__mastery-row {
  display: grid;
  grid-template-columns: 80px 1fr 40px 60px;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #a3b8d2;
}
.pp__mastery-name {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: #eef4ff;
  font-size: 10px;
}
.pp__mastery-bar {
  display: block;
  height: 6px;
  background: rgba(5, 9, 17, 0.85);
  border: 1px solid rgba(106, 216, 255, 0.25);
  border-radius: 1px;
  overflow: hidden;
}
.pp__mastery-bar-fill {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #1ea7ff, #6fdcff);
  box-shadow: 0 0 8px rgba(30,167,255,0.40);
}
.pp__mastery-pct {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  color: #ffe680;
  text-align: right;
}
.pp__mastery-played {
  font-size: 10px;
  color: #6a7d96;
  text-align: right;
}

.pp__empty {
  font-size: 11px;
  color: #6a7d96;
  font-style: italic;
}
.pp__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.pp__badge {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10px;
  padding: 4px 10px;
  border: 1px solid;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  clip-path: polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px);
}
.pp__badge--earned {
  color: #ffe680;
  border-color: #ffe680;
  background: rgba(255,230,128,0.10);
  box-shadow: 0 0 8px rgba(255,230,128,0.18);
}
.pp__badge--locked {
  color: #6a7d96;
  border-color: rgba(174,240,255,0.18);
  background: transparent;
}
.pp__challenge {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #a3b8d2;
}
.pp__challenge--done { color: #6ed788; }
.pp__challenge-check {
  font-size: 14px;
  width: 16px;
  text-align: center;
  flex-shrink: 0;
}
.pp__hist-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 11px;
  padding: 5px 0;
  border-bottom: 1px solid rgba(174,240,255,0.06);
}
.pp__hist-item:last-child { border-bottom: none; }
.pp__hist-outcome {
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 12px;
  font-weight: 700;
  width: 14px;
  text-align: center;
  flex-shrink: 0;
}
.pp__hist-item--win .pp__hist-outcome { color: #6fdcff; }
.pp__hist-item--loss .pp__hist-outcome { color: #ff7a5a; }
.pp__hist-detail {
  flex: 1;
  color: #a3b8d2;
}
.pp__hist-date {
  color: #6a7d96;
  font-size: 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}
.pp__close-btn {
  align-self: flex-end;
  padding: 8px 22px;
  background: rgba(10, 19, 34, 0.55);
  border: 1px solid rgba(174,240,255,0.28);
  clip-path: polygon(6px 0, 100% 0, 100% calc(100% - 6px), calc(100% - 6px) 100%, 0 100%, 0 6px);
  color: #eef4ff;
  font-family: 'Orbitron', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  position: relative;
  z-index: 1;
}
.pp__close-btn:hover {
  border-color: rgba(174,240,255,0.65);
  color: #fff;
  background: rgba(10, 19, 34, 0.85);
}

@media (max-width: 540px) {
  .pp__records-grid { grid-template-columns: repeat(2, 1fr); }
  .pp__header-title { font-size: 14px; letter-spacing: 0.12em; }
  .pp__rec-num { font-size: 14px; }
}
`;
  document.head.appendChild(style);
}
